import type { ReactNode, ReactElement } from 'react';

import { DialogOverlay, DialogContent } from '@reach/dialog';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { colors, media } from '../styles';

export type DialogPositionType = 'center' | 'top';

export interface DialogProps {
  children: ReactNode;
  ariaLabel: string;
  overlayColor?: string;
  position?: DialogPositionType;
  maxWidth?: string;
  className?: string;
  onClose: () => void;
  testId?: string;
  alignItemCenter?: boolean;
}

const S = {
  DialogOverlay: styled(DialogOverlay)<{ $overlayColor: string; $alignItemCenter: boolean }>`
    ${({ theme, $overlayColor, $alignItemCenter }) => css`
      position: fixed;
      inset: 0;
      top: var(--smart-banner-height);
      overflow: auto;
      z-index: 10007;

      ${$overlayColor &&
      css`
        background-color: ${$overlayColor};

        &:has(.product-subscription-overlay) {
          @media ${media(theme).lessThan('sm')} {
            background-color: ${theme.colors.neutral[0]};
          }
        }
      `}

      ${$alignItemCenter &&
      css`
        display: flex;
        align-items: center;
      `}
    `}
  `,

  DialogContent: styled(DialogContent)<{
    $verticalPosition: string;
    $maxWidth?: string;
  }>`
    ${({ theme, $verticalPosition, $maxWidth }) => css`
      background: ${theme.colors.neutral[0]};
      outline: none;
      padding: 0;
      width: 100%;
      max-width: ${$maxWidth || '750px'};
      margin: auto;
      position: relative;

      ${$verticalPosition === 'center' &&
      css`
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      `}

      @media ${media(theme).lessThan('sm')} {
        height: 100%;
      }
    `}
  `,

  DialogClose: styled.button`
    position: absolute;
    border: none;
    width: 42px;
    height: 42px;
    top: 0;
    right: 0;
    background-color: ${({ theme }) => theme.colors.content.component['dialog-close'].base};
    cursor: pointer;
    z-index: 1;

    &::before {
      color: ${({ theme }) => theme.colors.content.component['dialog-close'].color};
      font-family: gstar-icons;
      content: '${'\\F13F'}';
    }
  `,
};

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      children,
      overlayColor = colors.DIALOG_GREY,
      ariaLabel,
      position = 'center',
      maxWidth,
      onClose,
      className = '',
      testId = undefined,
      alignItemCenter = false,
    },
    ref
  ): ReactElement => (
    <S.DialogOverlay
      id="dialog-overlay"
      onDismiss={onClose}
      allowPinchZoom
      $overlayColor={overlayColor}
      data-testid={testId}
      $alignItemCenter={alignItemCenter}
    >
      <S.DialogContent
        ref={ref}
        aria-label={ariaLabel}
        $verticalPosition={position}
        className={className}
        $maxWidth={maxWidth}
      >
        <S.DialogClose
          type="button"
          aria-label="dialog close"
          className="close-dialog"
          onClick={onClose}
          data-testid="close-dialog"
        />
        {children}
      </S.DialogContent>
    </S.DialogOverlay>
  )
);

Dialog.displayName = 'Dialog';
